import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import moment from "moment";
import "moment/locale/fr";
import { useParams, useNavigate } from "react-router-dom";

function InlineEdit({ value, onSave, disabled, type = "text", multiline = false, className = "" }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value ?? "");

  const handleSave = () => {
    setEditing(false);
    const newVal = type === "number" ? parseFloat(text) || 0 : (typeof text === "string" ? text.trim() : text);
    if (String(newVal) !== String(value ?? "")) {
      onSave(newVal);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !multiline) handleSave();
    if (e.key === "Escape") { setText(value ?? ""); setEditing(false); }
  };

  if (editing) {
    if (multiline) {
      return (
        <textarea
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          rows={4}
          className={`w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#801111] focus:border-[#801111] resize-none ${className}`}
        />
      );
    }
    return (
      <input
        autoFocus
        type={type}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#801111] focus:border-[#801111] ${className}`}
      />
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      className="cursor-text hover:bg-gray-100 rounded px-1 py-0.5 -mx-1 transition-colors"
      title="Cliquer pour modifier"
    >
      {value != null && value !== "" ? value : <span className="text-gray-400 italic">—</span>}
    </span>
  );
}

moment.locale("fr");

const STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Confirmée",
  refused: "Refusée",
};

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-green-100 text-green-800",
  refused: "bg-red-100 text-red-800",
};

const STATUS_OPTIONS = Object.keys(STATUS_LABELS);

export default function OfferDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [offer, setOffer] = useState(null);
  const [author, setAuthor] = useState(null);
  const [client, setClient] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchOffer() {
      setLoading(true);
      const offerSnap = await getDoc(doc(db, "offers", id));
      if (!offerSnap.exists()) {
        setLoading(false);
        return;
      }
      const offerData = offerSnap.data();
      setOffer(offerData);
      setNewStatus(offerData.status);

      if (offerData.authorId) {
        const userSnap = await getDoc(doc(db, "users", offerData.authorId));
        if (userSnap.exists()) setAuthor({ id: offerData.authorId, ...userSnap.data() });
      }

      if (offerData.userId) {
        const clientSnap = await getDoc(doc(db, "users", offerData.userId));
        if (clientSnap.exists()) setClient({ id: offerData.userId, ...clientSnap.data() });
      }

      setLoading(false);
    }
    fetchOffer();
  }, [id]);

  const showNotif = (msg) => {
    const div = document.createElement("div");
    div.className =
      "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 text-sm";
    div.textContent = msg;
    document.body.appendChild(div);
    setTimeout(() => div.remove(), 3000);
  };

  const handleStatusChange = async (e) => {
    const val = e.target.value;
    setNewStatus(val);
    setSaving(true);
    try {
      await updateDoc(doc(db, "offers", id), { status: val });
      setOffer((prev) => ({ ...prev, status: val }));
      showNotif("Statut mis à jour !");
    } catch {
      alert("Erreur lors de la mise à jour du statut");
      setNewStatus(offer.status);
    }
    setSaving(false);
  };

  if (!offer)
    return (
      <PageTemplate
        loading={loading}
        content={
          <div className="text-center py-12">
            <p className="text-sm text-gray-500">Offre introuvable</p>
            <button
              onClick={() => navigate("/offers")}
              className="mt-2 text-xs font-medium text-[#801111] hover:text-[#5a0c0c]"
            >
              Retour à la liste
            </button>
          </div>
        }
      />
    );

  const UserCard = ({ label, user, userId }) => (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="px-3 py-2 border-b border-gray-200">
        <span className="text-xs font-medium text-gray-900">{label}</span>
      </div>
      <div className="p-3">
        {user ? (
          <div className="flex items-center gap-3">
            {user.photo ? (
              <img src={user.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold text-sm">
                {user.firstname?.[0]?.toUpperCase() || "?"}
              </div>
            )}
            <div className="flex-1 min-w-0 space-y-0.5">
              <p className="text-xs font-medium text-gray-900 truncate">
                {user.firstname} {user.lastname}
              </p>
              <p className="text-[10px] text-gray-500 truncate">{user.email}</p>
              {user.phoneNumber && (
                <p className="text-[10px] text-gray-500">{user.phoneNumber}</p>
              )}
            </div>
            {userId && (
              <button
                onClick={() => navigate(`/users/${userId}`)}
                className="text-xs font-medium text-[#801111] hover:text-[#5a0c0c] whitespace-nowrap"
              >
                Voir
              </button>
            )}
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic text-center py-2">Utilisateur inconnu</p>
        )}
      </div>
    </div>
  );

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="space-y-4">
          <button
            onClick={() => navigate("/offers")}
            className="text-xs font-medium text-[#801111] hover:text-[#5a0c0c]"
          >
            ← Retour aux offres
          </button>

          {/* Header */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[offer.status] || "bg-gray-100 text-gray-800"}`}>
                  {STATUS_LABELS[offer.status] || offer.status}
                </span>
                {offer.price != null && (
                  <span className="text-sm font-semibold text-gray-900">{offer.price} €</span>
                )}
                <span className="text-xs text-gray-500">
                  Créée le {offer.createdAt?.toDate ? moment(offer.createdAt.toDate()).format("DD/MM/YYYY HH:mm") : "-"}
                </span>
              </div>
              <span className="text-[10px] text-gray-400 font-mono">{id}</span>
            </div>
          </div>

          {/* Couturier + Client */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <UserCard label="Couturier (auteur)" user={author} userId={author?.id} />
            <UserCard label="Client" user={client} userId={client?.id} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Détails */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-3 py-2 border-b border-gray-200">
                <span className="text-xs font-medium text-gray-900">Détails de l'offre</span>
              </div>
              <div className="p-3 space-y-3">
                {/* Message */}
                <div>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Message</p>
                  <div className="text-xs text-gray-900 bg-gray-50 rounded p-2 whitespace-pre-wrap">
                    <InlineEdit
                      value={offer.message}
                      onSave={async (val) => {
                        try {
                          await updateDoc(doc(db, "offers", id), { message: val });
                          setOffer((prev) => ({ ...prev, message: val }));
                          showNotif("Message mis à jour !");
                        } catch { alert("Erreur lors de la mise à jour"); }
                      }}
                      multiline
                    />
                  </div>
                </div>

                {/* Infos */}
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Prix</span>
                    <span className="text-gray-900 font-medium">
                      <InlineEdit
                        value={offer.price}
                        type="number"
                        onSave={async (val) => {
                          try {
                            await updateDoc(doc(db, "offers", id), { price: val });
                            setOffer((prev) => ({ ...prev, price: val }));
                            showNotif("Prix mis à jour !");
                          } catch { alert("Erreur lors de la mise à jour"); }
                        }}
                      /> €
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Statut</span>
                    <select
                      value={newStatus}
                      onChange={handleStatusChange}
                      disabled={saving}
                      className="border border-gray-300 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:opacity-50"
                    >
                      {STATUS_OPTIONS.map((key) => (
                        <option key={key} value={key}>
                          {STATUS_LABELS[key]}
                        </option>
                      ))}
                    </select>
                  </div>
                  {offer.projectId && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Projet</span>
                      <span className="text-gray-900 font-mono text-[10px]">{offer.projectId}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Date création</span>
                    <span className="text-gray-900">
                      {offer.createdAt?.toDate
                        ? moment(offer.createdAt.toDate()).format("DD/MM/YYYY HH:mm")
                        : "-"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Photos */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-3 py-2 border-b border-gray-200">
                <span className="text-xs font-medium text-gray-900">Photos</span>
              </div>
              <div className="p-3">
                {offer.photos && offer.photos.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {offer.photos.map((url, idx) => (
                      <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="block">
                        <img src={url} alt="" className="w-full h-24 object-cover rounded border border-gray-200 hover:border-[#801111] transition-colors" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic text-center py-4">Aucune photo</p>
                )}
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
}
